import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useAuth } from "@/components/auth/AuthContext";
import { Button } from "@/components/ui/Button";
import { usePortal } from "@/lib/portal/PortalContext";
import { formatPrice } from "@/lib/formatPrice";
import { CustomerInvoicesError, fetchCustomerInvoices, fetchCustomerInvoiceLines, type PortalInvoiceOwner } from "@/lib/portal/invoicesApi";
import { invoiceRange, useInvoicesText, type InvoicesText } from "@/lib/portal/invoicesI18n";

export function CustomerInvoices() {
  const auth = useAuth();
  const portal = usePortal();
  const text = useInvoicesText();
  const heading = useId();
  // Only the validated auth profile's immutable (issuer, sub) can own a view.
  // Older auth providers missing either fail closed; never use email/customer id.
  const sub = auth.user && "sub" in auth.user ? auth.user.sub : undefined;
  const iss = auth.user && "issuer" in auth.user ? auth.user.issuer : undefined;
  const subject = typeof sub === "string" && sub.length > 0 && sub.length <= 255 && !/[\s\u0000-\u001f\u007f]/.test(sub) ? sub : null;
  const issuer = typeof iss === "string" && iss.length > 0 && !/[\s\u0000-\u001f\u007f]/.test(iss) ? iss : null;
  const expected = useMemo(() => subject && issuer ? Object.freeze({ sub: subject, issuer }) : null, [subject, issuer]);
  const owner = expected ? JSON.stringify([expected.issuer, expected.sub]) : null;
  const authenticated = auth.isReady && auth.isAuthenticated && auth.accountType === "customer" && owner;
  const resolved = !portal.loading && portal.isPortalUser && portal.customer;
  return (
    <section aria-labelledby={heading} className="space-y-4" data-testid="customer-invoices">
      <header><h2 id={heading} className="text-xl font-510 text-fg-primary">{text.title}</h2>
        <p className="text-sm text-fg-secondary">{text.intro}</p></header>
      {!auth.isReady || (authenticated && portal.loading) ? <p role="status">{text.loading}</p>
        : !authenticated ? <p role="status">{text.signIn}</p>
        : !resolved ? <p role="status">{text.unmapped}</p>
        : <InvoiceViewer key={owner} owner={owner!} expected={expected!} />}
    </section>
  );
}

type ReadState<T> = { owner: string; request: string; data: T | null; error: number | null };
function useOwnedRead<T>(owner: string, request: string, load: (signal: AbortSignal) => Promise<T>) {
  const [state, setState] = useState<ReadState<T>>({ owner, request, data: null, error: null });
  useEffect(() => {
    const controller = new AbortController();
    setState({ owner, request, data: null, error: null });
    load(controller.signal).then((data) => {
      if (!controller.signal.aborted) setState({ owner, request, data, error: null });
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) setState({ owner, request, data: null, error: error instanceof CustomerInvoicesError ? error.status : 0 });
    });
    return () => controller.abort();
  }, [owner, request, load]);
  // Render-time ownership is essential: effect cleanup alone leaves one stale paint.
  return state.owner === owner && state.request === request ? state : { data: null, error: null };
}

function ReadError({ status, retry }: { status: number; retry: () => void }) {
  const text = useInvoicesText();
  const message = status === 401 ? text.unauthorized : status === 403 ? text.forbidden
    : status === 404 ? text.notFound : status === 503 ? text.unavailable : text.failed;
  return <div role="alert" className="space-y-2 rounded border border-accent-rose/30 p-3">
    <p>{message}</p><Button variant="secondary" size="sm" onClick={retry}>{text.retry}</Button>
  </div>;
}

function Pager({ offset, count, total, limit, label, onPage }: {
  offset: number; count: number; total: number; limit: number; label: string; onPage: (offset: number) => void;
}) {
  const text = useInvoicesText();
  return <nav aria-label={label} className="flex flex-wrap items-center gap-3 text-sm">
    <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - limit))}>{text.previous}</Button>
    <span role="status">{invoiceRange(text, offset, count, total)}</span>
    <Button variant="secondary" size="sm" disabled={offset + limit >= total} onClick={() => onPage(offset + limit)}>{text.next}</Button>
  </nav>;
}

function InvoiceViewer({ owner, expected }: { owner: string; expected: PortalInvoiceOwner }) {
  const text = useInvoicesText();
  const [offset, setOffset] = useState(0);
  const [retry, setRetry] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const load = useCallback((signal: AbortSignal) => fetchCustomerInvoices(expected, offset, signal), [expected, offset]);
  const list = useOwnedRead(owner, `${offset}:${retry}`, load);
  if (list.error !== null) return <ReadError status={list.error} retry={() => setRetry((n) => n + 1)} />;
  if (!list.data) return <p role="status">{text.loading}</p>;
  const page = list.data;
  return <div className="space-y-4">
    {page.rows.length === 0 ? <p role="status">{text.empty}</p> : <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {page.rows.map((invoice) => <article key={invoice.invoice_number} className="min-w-0 space-y-3 rounded-lg border border-border bg-bg-surface p-4">
        <Button variant="ghost" className="h-auto max-w-full whitespace-normal break-all text-left" aria-pressed={selected === invoice.invoice_number}
          onClick={() => setSelected(invoice.invoice_number)}>{invoice.invoice_number}</Button>
        <dl className="space-y-1 text-sm"><div><dt className="text-fg-secondary">{text.date}</dt>
          <dd>{invoice.invoice_date ? <time dateTime={invoice.invoice_date}>{invoice.invoice_date}</time> : text.missing}</dd></div>
          <div><dt className="text-fg-secondary">{text.total}</dt><dd>{invoice.total_tvac !== null ? formatPrice(Number(invoice.total_tvac), "EUR") : text.missing}</dd></div></dl>
      </article>)}
    </div>}
    <Pager label={text.invoicesPagination} offset={page.offset} count={page.rows.length} total={page.total} limit={50}
      onPage={(next) => { setSelected(null); setOffset(next); }} />
    {selected !== null && <InvoiceLines key={selected} owner={owner} expected={expected} invoiceNumber={selected} close={() => setSelected(null)} text={text} />}
  </div>;
}

function InvoiceLines({ owner, expected, invoiceNumber, close, text }: { owner: string; expected: PortalInvoiceOwner; invoiceNumber: string; close: () => void; text: InvoicesText }) {
  const heading = useId();
  const [offset, setOffset] = useState(0);
  const [retry, setRetry] = useState(0);
  const load = useCallback((signal: AbortSignal) => fetchCustomerInvoiceLines(expected, invoiceNumber, offset, signal), [expected, invoiceNumber, offset]);
  const detail = useOwnedRead(owner, `${invoiceNumber}:${offset}:${retry}`, load);
  return <section aria-labelledby={heading} className="space-y-3 rounded-lg border border-border p-4" data-testid="customer-invoice-detail">
    <header className="flex flex-wrap items-center justify-between gap-2"><h3 id={heading} className="min-w-0 break-all font-510">{text.lines}: {invoiceNumber}</h3>
      <Button variant="secondary" size="sm" onClick={close}>{text.close}</Button></header>
    {detail.error !== null ? <ReadError status={detail.error} retry={() => setRetry((n) => n + 1)} />
      : !detail.data ? <p role="status">{text.loading}</p> : <>
        {detail.data.lines.length === 0 ? <p role="status">{text.emptyLines}</p> : <div className="overflow-x-auto">
          <table aria-label={text.lines} className="w-full text-left text-sm">
            <thead><tr><th scope="col" className="p-2">{text.product}</th><th scope="col" className="p-2">{text.quantity}</th><th scope="col" className="p-2">{text.serial}</th></tr></thead>
            <tbody>{detail.data.lines.map((line, index) => <tr key={`${line.seq}:${index}`} className="border-t border-border align-top">
              <td className="p-2"><p>{line.label ?? text.missing}</p>{line.ean !== null && <p className="break-all text-fg-secondary">{text.ean}: {line.ean}</p>}</td>
              <td className="p-2 font-mono">{line.quantity ?? text.missing}</td>
              <td className="max-w-xs whitespace-pre-wrap break-all p-2 font-mono">{line.serial_number === null || line.serial_number === "" ? text.sourceSerialNotRecorded : line.serial_number}</td>
            </tr>)}</tbody>
          </table></div>}
        <Pager label={text.linesPagination} offset={detail.data.offset} count={detail.data.lines.length} total={detail.data.total} limit={100} onPage={setOffset} />
      </>}
  </section>;
}
